import { useCallback, useImperativeHandle, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { FileUploadIcon } from '@hugeicons-pro/core-twotone-rounded'
import { Box, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'

const classes = {
    root: {
        background: '#FFFFFF',
        border: (theme) => `1px dashed ${theme.palette.secondary.main}`,
        borderRadius: 2,
        cursor: 'pointer',
        minWidth: '300px',
        maxWidth: '460px',
        flex: 1,
    },
}

const UploadFilesCard = ({ isUploading, onFilesChange, ref }) => {
    const { t } = useTranslation()
    const theme = useTheme()

    const [filesToUpload, setFilesToUpload] = useState([])

    const handleAcceptedFiles = useCallback(
        (acceptedFiles) => {
            const newFiles = acceptedFiles.filter((file) => !filesToUpload.some((f) => f.name === file.name))
            const updatedFiles = [...filesToUpload, ...newFiles]

            setFilesToUpload(updatedFiles)
            onFilesChange(updatedFiles)
        },
        [filesToUpload, onFilesChange]
    )

    const onDrop = useCallback(
        (acceptedFiles) => {
            handleAcceptedFiles(acceptedFiles)
        },
        [handleAcceptedFiles]
    )

    const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
        onDrop,
        accept: {
            'image/vnd.dxf': ['.dxf'],
            'image/vnd.dwg': ['.dwg'],
        },
        disabled: isUploading,
        noClick: true,
    })

    useImperativeHandle(ref, () => ({
        removeFile(fileToRemove) {
            const updatedFiles = filesToUpload.filter((file) => file.name !== fileToRemove.name)

            setFilesToUpload(updatedFiles)
            onFilesChange(updatedFiles)
        },
        handleCleanUploadList() {
            setFilesToUpload([])
            onFilesChange([])
        },
    }))

    return (
        <Box
            {...getRootProps()}
            sx={[
                {
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                    padding: 3,
                    gap: 1.5,
                },
                classes.root,
            ]}
        >
            <input {...getInputProps()} />

            <HugeiconsIcon
                color={theme.palette.secondary.main}
                icon={FileUploadIcon}
                size={64}
            />
            <Box
                sx={[
                    {
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    },
                    classes.dropTextContainer,
                ]}
            >
                <Typography
                    align="center"
                    variant="body1"
                >
                    {t(isDragActive ? 'Drop files here...' : 'Drop your DXF or DWG files here to get started')}
                </Typography>

                <Button
                    color="primary"
                    size="medium"
                    variant="contained"
                    onClick={open}
                >
                    {t('Browse files')}
                </Button>
                <Typography
                    align="center"
                    variant="small"
                >
                    {t('Upload one or many 2D CAD files to begin your order. Supported formats include DXF and DWG.')}
                </Typography>
            </Box>
        </Box>
    )
}

UploadFilesCard.propTypes = {
    isUploading: PropTypes.bool,
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    onFilesChange: PropTypes.func,
}

export default UploadFilesCard
